import { createGroup, fetchGroup, fetchUsers, updateGroup } from '@app/modules/admin/api';
import { Group, mapPromises, User, UserRole } from '@mi-front/core';
import { useToast } from '@app/core/notifications';
import { AsyncView, Form, SelectAllOrNone, SelectField, TextField } from '@mi-front/ui';
import pick from 'lodash/pick';
import { Grid } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import * as yup from 'yup';
import { GroupForSave } from '../../../../../core/types/users';
import { getAppUserDisplayName } from '../../../../../helpers/user';
import { groupNotificationMessages } from './messages';

type GroupForForm = Pick<Group, 'id' | 'name'> & {
  readonly users: readonly User[];
};

interface Props {
  readonly data: {
    readonly group: GroupForForm;
    readonly availableUsers: readonly User[];
  };
  readonly save: (data: GroupForForm) => Promise<any>;
  readonly onSuccess: (data: Group) => any;
  readonly cancel: () => any;
}

export function GroupForm(p: Props) {
  const { group: initialValues, availableUsers } = p.data;
  const { $t } = useIntl();
  const { notifySuccess } = useToast();

  return (
    <Form
      initialValues={initialValues}
      validationSchema={yup.object().shape({
        name: yup.string().required(),
        users: yup.array(yup.mixed()),
      })}
      onSubmit={(data) => p.save(data)}
      onSuccess={(data) => {
        notifySuccess(
          initialValues.name
            ? $t(groupNotificationMessages.updated)
            : $t(groupNotificationMessages.created),
        );
        p.onSuccess(data);
      }}
      onCancel={p.cancel}
      useUpdateLabels={!!initialValues.name}
    >
      {(p) => {
        return (
          <>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6} lg={5}>
                <TextField
                  markRequired
                  name="name"
                  label={$t({
                    id: 'admin.groups.form.name.label',
                    defaultMessage: 'Nombre',
                  })}
                  placeholder={$t({
                    id: 'admin.groups.form.name.placeholder',
                    defaultMessage: 'Por ejemplo "Usuarios de Emisoras Principales"',
                  })}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={12} md={12} lg={10}>
                <SelectAllOrNone
                  onSelectAll={() => p.setFieldValue('users', availableUsers)}
                  onSelectNone={() => p.setFieldValue('users', [])}
                >
                  <SelectField
                    multiple
                    id="select-users"
                    name="users"
                    label={$t({
                      id: 'admin.groups.form.users.label',
                      defaultMessage: 'Usuarios asignados',
                    })}
                    noOptionsMessage={() =>
                      $t({
                        id: 'admin.groups.form.users.noMore',
                        defaultMessage: 'No hay más usuarios disponibles',
                      })
                    }
                    options={availableUsers}
                    getOptionLabel={(u) => getAppUserDisplayName(u)}
                    getOptionValue={(u) => u.id}
                    hint={
                      <div className="text-warning">
                        <FormattedMessage
                          id="admin.groups.form.users.help"
                          defaultMessage="Los usuarios pueden pertenecer a sólo un grupo. Los usuarios seleccionados que estén vinculados a otros grupos serán sobreescritos."
                        />
                      </div>
                    }
                  />
                </SelectAllOrNone>
              </Grid>
            </Grid>
          </>
        );
      }}
    </Form>
  );
}

export const ConnGroupForm = (p: {
  readonly id?: Group['id'];
  readonly onSuccess: (data: Group) => any;
  readonly cancel: () => any;
}) => {
  const NEW_GROUP: GroupForForm = {
    id: '',
    name: '',
    users: [],
  };

  return (
    <AsyncView
      promiseFn={() =>
        mapPromises({
          group: p.id ? fetchGroup(p.id) : Promise.resolve(NEW_GROUP),
          availableUsers: fetchUsers({ pageSize: 999 }).then(({ results }) =>
            results.filter((u) => u.role !== UserRole.CLIENT_ADMIN),
          ),
        })
      }
    >
      {(pp) => (
        <GroupForm
          {...pp}
          save={async (data) => {
            const dataToSave: GroupForSave = {
              ...pick(data, ['name']),
              usersIds: data.users.map((u) => u.id),
            };

            return p.id ? await updateGroup(p.id, dataToSave) : await createGroup(dataToSave);
          }}
          onSuccess={p.onSuccess}
          cancel={p.cancel}
        />
      )}
    </AsyncView>
  );
};
